import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import {
    Menu,
    X,
    Minus,
    Square,
    AppWindow,
    Plug,
    Activity,
    Monitor,
    Settings as SettingsIcon,
    LayoutDashboard,
    PlusCircle,
    Boxes,
} from "lucide-react";

/**
 * Shared chrome for the public marketing pages (Landing, Capabilities, ...):
 * nav, footer, brand mark, the "native Windows app" mockup chrome, and the
 * SectionHeader primitive. Extracted out of Landing.jsx so a second
 * marketing page doesn't have to duplicate ~250 lines of nav/footer/mockup
 * scaffolding, or drift out of sync with it over time.
 *
 * "Capabilities"/"How It Works"/"Use Cases" are sections that only live on
 * the landing page itself, so from any OTHER marketing page those nav links
 * point at "/#capabilities" etc. (a real navigation + hash jump) rather than
 * a bare "#capabilities" anchor, which would just no-op on a page that
 * doesn't have that section.
 */
export const DOWNLOAD_HREF = "/register";

export function BrandMark({ size = "h-[83px]" }) {
    return (
        <img
            src="/brand/moovbytes-logo-white.png"
            alt="MoovBytes"
            className={`${size} w-auto select-none object-contain`}
        />
    );
}

export function TopBar({ mobileOpen, setMobileOpen }) {
    const [scrolled, setScrolled] = useState(false);
    const { pathname, hash } = useLocation();
    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 8);
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    // These all point back at a section on the one-page Home
    // (see public/landing-template/landing.html) rather than a separate
    // route -- Capabilities/Documentation/About/UseCases/HowItWorks/
    // Support no longer have their own standalone pages to link to.
    const NAV = [
        { to: "/", label: "Product", route: true },
        { to: "/#features-sec", label: "Capabilities", route: true },
        { to: "/#about-sec", label: "How It Works", route: true },
        { to: "/#testi-sec", label: "Use Cases", route: true },
        { to: "/#blog-sec", label: "Documentation", route: true },
        { to: "/#contact-sec", label: "Support", route: true },
    ];

    return (
        <header
            id="top"
            className={`sticky top-0 z-40 transition-all duration-300 ${
                scrolled
                    ? "border-b border-white/10 bg-[#050e22]/85 backdrop-blur-xl"
                    : "bg-transparent"
            }`}
            data-testid="landing-topbar"
        >
            <div className="mx-auto flex h-24 max-w-[1600px] items-center justify-between px-6 sm:px-10">
                <Link to="/" data-testid="landing-home-link">
                    <BrandMark />
                </Link>

                <nav className="hidden items-center gap-7 lg:flex" data-testid="landing-nav">
                    {NAV.map((n) =>
                        n.route ? (
                            <Link
                                key={n.label}
                                to={n.to}
                                data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
                                className={`text-[13px] font-medium transition ${
                                    pathname + hash === n.to ? "text-[#3fb8ff]" : "text-white/65 hover:text-white"
                                }`}
                            >
                                {n.label}
                            </Link>
                        ) : n.action ? (
                            <button
                                key={n.label}
                                type="button"
                                onClick={n.action}
                                data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
                                className="text-[13px] font-medium text-white/65 transition hover:text-white"
                            >
                                {n.label}
                            </button>
                        ) : (
                            <a
                                key={n.label}
                                href={n.to}
                                data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
                                className="text-[13px] font-medium text-white/65 transition hover:text-white"
                            >
                                {n.label}
                            </a>
                        ),
                    )}
                </nav>

                <div className="hidden lg:flex">
                    <Link
                        to={DOWNLOAD_HREF}
                        data-testid="landing-download-btn"
                        className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-[#00C2FF] to-[#0066ff] px-4 py-2.5 text-[13px] font-semibold text-white shadow-md shadow-[#004AAD]/25 transition hover:brightness-110"
                    >
                        <AppWindow size={15} />
                        Download for Windows
                    </Link>
                </div>

                <button
                    className="rounded-md border border-white/10 bg-white/[0.04] p-2 text-white/80 lg:hidden"
                    onClick={() => setMobileOpen((v) => !v)}
                    aria-label="Menu"
                    data-testid="landing-mobile-toggle"
                >
                    {mobileOpen ? <X size={17} /> : <Menu size={17} />}
                </button>
            </div>

            {mobileOpen && (
                <div className="border-t border-white/10 bg-[#050e22]/95 backdrop-blur-xl lg:hidden" data-testid="landing-mobile-menu">
                    <div className="mx-auto max-w-[1600px] px-6 py-4">
                        {NAV.map((n) =>
                            n.route ? (
                                <Link
                                    key={n.label}
                                    to={n.to}
                                    onClick={() => setMobileOpen(false)}
                                    className="block py-2.5 text-[13px] font-medium text-white/75"
                                >
                                    {n.label}
                                </Link>
                            ) : n.action ? (
                                <button
                                    type="button"
                                    key={n.label}
                                    onClick={() => { setMobileOpen(false); n.action(); }}
                                    className="block py-2.5 text-left text-[13px] font-medium text-white/75"
                                >
                                    {n.label}
                                </button>
                            ) : (
                                <a
                                    key={n.label}
                                    href={n.to}
                                    onClick={() => setMobileOpen(false)}
                                    className="block py-2.5 text-[13px] font-medium text-white/75"
                                >
                                    {n.label}
                                </a>
                            ),
                        )}
                        <div className="mt-3 border-t border-white/10 pt-4">
                            <Link
                                to={DOWNLOAD_HREF}
                                onClick={() => setMobileOpen(false)}
                                className="flex items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-[#00C2FF] to-[#0066ff] px-4 py-2.5 text-center text-[13px] font-semibold text-white"
                            >
                                <AppWindow size={15} />
                                Download for Windows
                            </Link>
                        </div>
                    </div>
                </div>
            )}
        </header>
    );
}

export function FooterBar({ openContact }) {
    return (
        <footer className="relative z-10 border-t border-white/5 bg-[#020c1d] px-6 py-14 sm:px-6" data-testid="landing-footer">
            <div className="mx-auto max-w-[1600px]">
                <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-4">
                    <div className="lg:col-span-1">
                        <BrandMark size="h-[73px]" />
                        <p className="mt-3 max-w-xs text-[13px] leading-relaxed text-white/50">
                            Professional Windows migration software for PCs,
                            servers, NAS systems, and cloud storage.
                        </p>
                    </div>
                    <div>
                        <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-white/40">Product</div>
                        <ul className="mt-3 space-y-2 text-[13px] text-white/65">
                            <li><Link to={DOWNLOAD_HREF} className="transition hover:text-white">Download</Link></li>
                            <li><Link to="/#features-sec" className="transition hover:text-white">Capabilities</Link></li>
                            <li><Link to="/#testi-sec" className="transition hover:text-white">Use Cases</Link></li>
                        </ul>
                    </div>
                    <div>
                        <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-white/40">Resources</div>
                        <ul className="mt-3 space-y-2 text-[13px] text-white/65">
                            <li><Link to="/#blog-sec" className="transition hover:text-white">Documentation</Link></li>
                            <li>
                                <button type="button" onClick={openContact} className="text-left transition hover:text-white">
                                    Support
                                </button>
                            </li>
                        </ul>
                    </div>
                    <div>
                        <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-white/40">Company</div>
                        <ul className="mt-3 space-y-2 text-[13px] text-white/65">
                            <li><Link to="/" className="transition hover:text-white">About</Link></li>
                            <li>
                                <button type="button" onClick={openContact} className="text-left transition hover:text-white">
                                    Contact
                                </button>
                            </li>
                        </ul>
                    </div>
                </div>

                <div className="mt-12 flex flex-col items-center justify-between gap-4 border-t border-white/5 pt-6 sm:flex-row">
                    <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/35">© {new Date().getFullYear()} MoovBytes</span>
                    <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-white/40">
                        <span className="h-1.5 w-1.5 rounded-full bg-[#10b981]" />
                        All systems operational
                    </div>
                </div>
            </div>
        </footer>
    );
}

export function SectionHeader({ eyebrow, title, body, align = "center" }) {
    const isLeft = align === "left";
    return (
        <div className={isLeft ? "max-w-xl text-left" : "text-center"}>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#3fb8ff]">{eyebrow}</div>
            <h2 className="mt-3 font-display text-4xl font-bold tracking-tight sm:text-5xl">{title}</h2>
            {body && (
                <p className={`mt-4 text-[15px] leading-relaxed text-white/65 ${isLeft ? "max-w-xl" : "mx-auto max-w-2xl"}`}>{body}</p>
            )}
        </div>
    );
}

/* Native-Windows-style chrome for product mockups -- matches the real
   Electron desktop app's own titleBarOverlay treatment (see
   desktop/main.js): dark title bar, minimize/maximize/close on the
   right, product icon + name on the left. Deliberately not a browser
   window (no URL bar). */
export function WindowsChrome({ title = "MoovBytes", children, className = "" }) {
    return (
        <div className={`overflow-hidden rounded-xl border border-white/10 bg-[#0a1228] shadow-[0_40px_100px_-30px_rgba(0,0,0,0.75)] ${className}`}>
            <div className="flex items-center justify-between border-b border-white/10 bg-[#0a1228] px-3 py-2">
                <div className="flex items-center gap-2 pl-1">
                    <img src="/brand/moovbytes-icon.png" alt="" className="h-3.5 w-3.5 rounded" />
                    <span className="text-[11px] font-medium text-white/60">{title}</span>
                </div>
                <div className="flex items-center gap-3 text-white/35">
                    <Minus size={11} />
                    <Square size={9} />
                    <X size={12} />
                </div>
            </div>
            <div className="p-4">{children}</div>
        </div>
    );
}

/**
 * The abstract blue gradient-orb visual used in place of real photography
 * on several page heroes (Documentation, About, ...). Reused rather than
 * duplicated per-page: no real licensed stock photos exist for what the
 * original reference mockups showed (office interiors, data centers,
 * etc.), so every marketing-page hero uses this same illustrative
 * treatment instead -- consistent with the rest of the site's abstract
 * dark-navy/cyan visual language rather than photography it can't have.
 */
export function GradientOrbVisual({ heading, body }) {
    return (
        <div className="relative hidden lg:block">
            <div className="pointer-events-none absolute -inset-10 -z-10 rounded-full bg-[#0066ff]/20 blur-[100px]" />
            <div
                className="aspect-square w-full rounded-3xl opacity-90"
                style={{ background: "radial-gradient(circle at 35% 30%, #3fb8ff, #0a4fd6 45%, #050e22 80%)" }}
            />
            {(heading || body) && (
                <div className="absolute bottom-6 right-6 text-right">
                    {heading && <div className="font-display text-xl font-bold leading-tight text-white">{heading}</div>}
                    {body && <p className="mt-2 max-w-[220px] text-[12px] leading-relaxed text-white/60">{body}</p>}
                </div>
            )}
        </div>
    );
}

export const SIDEBAR_ITEMS = [
    { label: "Dashboard", icon: LayoutDashboard },
    { label: "New Migration", icon: PlusCircle },
    { label: "Migrations", icon: Boxes },
    { label: "Devices", icon: Monitor },
    { label: "Connections", icon: Plug },
    { label: "Activity", icon: Activity },
    { label: "Settings", icon: SettingsIcon },
];

export function AppSidebar({ active = "Dashboard" }) {
    return (
        <div className="w-[104px] shrink-0 space-y-0.5 border-r border-white/5 pr-2">
            {SIDEBAR_ITEMS.map((it) => (
                <div
                    key={it.label}
                    className={`flex items-center gap-1.5 rounded px-2 py-1.5 text-[9px] font-medium ${
                        it.label === active ? "bg-[#00C2FF]/15 text-[#3fb8ff]" : "text-white/45"
                    }`}
                >
                    <it.icon size={11} />
                    <span className="truncate">{it.label}</span>
                </div>
            ))}
        </div>
    );
}
